import { Tab, TabPanel, Tabs, TabsList } from "@mui/base";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { usePendingOrders } from "~/components/services/hooks/useOrdersWithPermissions";
import { PendingOrder } from "~/components/services/orders/PendingOrder";
import {
  ORDERS_TAB_PARAM,
  OrdersTab,
  parseOrdersTab,
} from "~/components/services/orders/ordersTab";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider";
import { usePageTitle } from "~/hooks/usePageTitle";
import { orderQueries } from "~/pages/services/queryKeys";

import { apiClient } from "../../api/ApiClient";
import OrdersTable from "../../components/services/orders/Orders";
import { PaginationConfig } from "../../components/ui/pagination/Pagination";
import styles from "./OrdersPage.module.scss";

const labels: Record<OrdersTab, string> = {
  links: "Link Building",
  concierge: "Concierge Setup",
};

const OrdersPage = () => {
  usePageTitle(PAGE_TITLES.pastOrders);

  const { team } = useTeam();
  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 10,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });

  // In the URL: back from paying, the paid order's tab opens.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = parseOrdersTab(searchParams.get(ORDERS_TAB_PARAM));

  const { data: ordersData, isLoading: isLoadingOrders } = useQuery({
    queryKey: orderQueries.list({
      teamId: team?.id,
      type: selectedTab,
      page: pagination.currentPage,
      size: pagination.itemsPerPage,
    }),
    queryFn: async () => {
      const response = await apiClient.billing.ordersList({
        type: selectedTab,
        "page[size]": pagination.itemsPerPage,
        "page[number]": pagination.currentPage,
        s: "created_at DESC, completed_at DESC, paid_at DESC",
        q: {
          status_eq: "succeeded",
        },
      });
      const { headers } = response;

      setPagination((prev: PaginationConfig) => ({
        ...prev,
        totalItems: Number(headers.get("x-pager-total-count")),
        nextPage: Number(headers.get("x-pager-next-page")),
        prevPage: Number(headers.get("x-pager-prev-page")),
        totalPages: Number(headers.get("x-pager-total-pages")),
      }));

      return response.data?.["payments/orders"];
    },
  });

  const { orders: pendingOrders, refetch: refetchPendingOrders } =
    usePendingOrders();
  const pendingOrder = pendingOrders[0];

  return (
    <div className={styles.page}>
      {pendingOrder && (
        <PendingOrder
          order={pendingOrder}
          refetchPendingOrders={refetchPendingOrders}
        />
      )}
      <h1 className={styles.title}>{PAGE_TITLES.pastOrders}</h1>
      <Tabs
        value={selectedTab}
        onChange={(_event, newValue) => {
          if (typeof newValue === "string") {
            setSearchParams(
              { [ORDERS_TAB_PARAM]: parseOrdersTab(newValue) },
              { replace: true },
            );
          }
        }}
      >
        <TabsList className={styles.tabsList}>
          <Tab className={styles.tabButton} value="links">
            {labels.links}
          </Tab>
          <Tab className={styles.tabButton} value="concierge">
            {labels.concierge}
          </Tab>
        </TabsList>
        <TabPanel value="links">
          <OrdersTable
            data={ordersData ?? []}
            isLoading={isLoadingOrders}
            pagination={pagination}
            setPagination={setPagination}
          />
        </TabPanel>
        <TabPanel value="concierge">
          <OrdersTable
            data={ordersData ?? []}
            isLoading={isLoadingOrders}
            pagination={pagination}
            setPagination={setPagination}
          />
        </TabPanel>
      </Tabs>
    </div>
  );
};

export default OrdersPage;
